import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Boundary,
  DiagnosticsRecord,
  EditingProject,
  TrimSnapshot,
  ViewportState,
} from "../types/project";
import { formatFrames } from "../core/time";
import { setBoundary } from "../core/trim";
import {
  commitHistory,
  EMPTY_HISTORY,
  redoHistory,
  undoHistory,
  type HistoryState,
} from "../core/history";
import { PlaybackController, type PlaybackState } from "../audio/playback";
import { WaveformService } from "../waveform/service";
import type { WaveformPyramid } from "../waveform/types";
import { useDetailWaveform } from "../waveform/useDetailWaveform";
import { WaveformCanvas } from "./WaveformCanvas";
import { PrecisionEditor } from "./PrecisionEditor";
import { ExportSheet } from "./ExportSheet";
import type { ExportResult } from "../media/exporter";

type Props = {
  project: EditingProject;
  file: File;
  onProjectChange: (project: EditingProject) => void;
  onBack: () => void;
  onDiagnosticsChange: (patch: Partial<DiagnosticsRecord>) => void;
  onError: (message: string) => void;
};

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export function Editor({
  project,
  file,
  onProjectChange,
  onBack,
  onDiagnosticsChange,
  onError,
}: Props) {
  const playback = useMemo(() => new PlaybackController(), []);
  const waveformService = useMemo(() => new WaveformService(), []);
  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    currentFrame: project.playheadFrame,
    playing: false,
    buffering: false,
    error: null,
  });
  const [viewport, setViewport] = useState<ViewportState>(project.viewport);
  const [overview, setOverview] = useState<WaveformPyramid | null>(null);
  const [history, setHistory] = useState<HistoryState>(EMPTY_HISTORY);
  const [precisionBoundary, setPrecisionBoundary] = useState<Boundary | null>(
    null,
  );
  const [showExport, setShowExport] = useState(false);
  const [waveformProgress, setWaveformProgress] = useState(0);
  const projectRef = useRef(project);
  const reportedPlaybackError = useRef<string | null>(null);
  const persistTimer = useRef<number | null>(null);
  const precisionDetailTiming = useCallback(
    (seconds: number) => onDiagnosticsChange({ detailSeconds: seconds }),
    [onDiagnosticsChange],
  );

  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  useEffect(() => {
    playback.load(
      file,
      project.sampleRate,
      project.durationFrames,
      project.trackStartSeconds,
      project.playheadFrame,
    );
    playback.setRate(project.playbackRate);
    const unsubscribe = playback.subscribe((state) => {
      setPlaybackState(state);
      if (state.playing) {
        setViewport((current) => ({
          ...current,
          centerFrame: state.currentFrame,
        }));
      } else {
        if (persistTimer.current !== null)
          window.clearTimeout(persistTimer.current);
        persistTimer.current = window.setTimeout(() => {
          const current = projectRef.current;
          if (current.playheadFrame !== state.currentFrame) {
            onProjectChange({
              ...current,
              playheadFrame: state.currentFrame,
              updatedAt: Date.now(),
            });
          }
        }, 200);
      }
    });
    return () => {
      unsubscribe();
      playback.dispose();
      if (persistTimer.current !== null)
        window.clearTimeout(persistTimer.current);
    };
    // Loading belongs to this file/project identity only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playback, file, project.id]);

  useEffect(() => () => waveformService.dispose(), [waveformService]);

  useEffect(() => {
    waveformService.setInteractivePlayback(
      playbackState.playing ||
        playbackState.buffering ||
        precisionBoundary !== null,
    );
    return () => waveformService.setInteractivePlayback(false);
  }, [
    playbackState.buffering,
    playbackState.playing,
    precisionBoundary,
    waveformService,
  ]);

  useEffect(() => {
    if (playbackState.error === reportedPlaybackError.current) return;
    reportedPlaybackError.current = playbackState.error;
    if (playbackState.error) onError(playbackState.error);
  }, [onError, playbackState.error]);

  useEffect(() => {
    let active = true;
    setOverview(null);
    void waveformService
      .analyzeOverview(file, project, (partial) => {
        if (!active) return;
        setOverview(partial);
        setWaveformProgress(partial.progress);
      })
      .then((result) => {
        if (!active) return;
        setOverview(result.pyramid);
        setWaveformProgress(1);
        onDiagnosticsChange({ overviewSeconds: result.elapsedMs / 1000 });
      })
      .catch((error: unknown) => active && onError(messageOf(error)));
    return () => {
      active = false;
    };
    // Whole-file analysis must not restart for every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waveformService, file, project.id]);

  const detailView = useDetailWaveform({
    file,
    project,
    service: waveformService,
    centerFrame: viewport.centerFrame,
    visibleFrames: viewport.visibleFrames,
    overview,
    debounceMs: 100,
    onTiming: precisionDetailTiming,
    enabled: precisionBoundary === null,
  });

  const persistViewport = useCallback(
    (next: ViewportState) => {
      setViewport(next);
      const current = projectRef.current;
      window.clearTimeout(persistTimer.current ?? undefined);
      persistTimer.current = window.setTimeout(
        () =>
          onProjectChange({
            ...current,
            viewport: next,
            updatedAt: Date.now(),
          }),
        350,
      );
    },
    [onProjectChange],
  );

  const seek = useCallback(
    (frame: number) => {
      playback.seek(frame);
      setViewport((current) => ({
        ...current,
        centerFrame: playback.currentFrame,
      }));
    },
    [playback],
  );

  const updateTrim = (
    next: TrimSnapshot,
    label: string,
    boundary?: Boundary,
  ): void => {
    const before = {
      startFrame: project.startFrame,
      endFrame: project.endFrame,
    };
    setHistory((current) =>
      commitHistory(current, { label, before, after: next, boundary }),
    );
    onProjectChange({ ...project, ...next, updatedAt: Date.now() });
  };

  const assignBoundary = (boundary: Boundary): void => {
    const next = setBoundary(
      project,
      boundary,
      playback.currentFrame,
      project.durationFrames,
    );
    updateTrim(next, `${boundary.toUpperCase()}位置変更`, boundary);
    navigator.vibrate?.(8);
  };

  const undo = (): void => {
    const result = undoHistory(history, project);
    setHistory(result.state);
    onProjectChange({ ...project, ...result.value, updatedAt: Date.now() });
  };

  const redo = (): void => {
    const result = redoHistory(history, project);
    setHistory(result.state);
    onProjectChange({ ...project, ...result.value, updatedAt: Date.now() });
  };

  const confirmPrecision = (frame: number): void => {
    if (!precisionBoundary) return;
    const next = setBoundary(
      project,
      precisionBoundary,
      frame,
      project.durationFrames,
    );
    updateTrim(
      next,
      `${precisionBoundary.toUpperCase()}位置変更`,
      precisionBoundary,
    );
    setPrecisionBoundary(null);
  };

  const setRate = (rate: EditingProject["playbackRate"]): void => {
    playback.setRate(rate);
    onProjectChange({ ...project, playbackRate: rate, updatedAt: Date.now() });
  };

  const previewSelection = (mode: "all" | "start" | "end"): void => {
    const fiveSeconds = project.sampleRate * 5;
    const start =
      mode === "end"
        ? Math.max(project.startFrame, project.endFrame - fiveSeconds)
        : project.startFrame;
    const end =
      mode === "start"
        ? Math.min(project.endFrame, project.startFrame + fiveSeconds)
        : project.endFrame;
    void playback.playRange(start, end, false);
  };

  const exportCompleted = (result: ExportResult): void => {
    onDiagnosticsChange({
      outputCodec: `H.264 avc1 + AAC-LC (${result.aacMode})`,
      outputDurationSeconds: result.durationFrames / project.sampleRate,
      exportSeconds: result.elapsedSeconds,
      outputBytes: result.file.size,
    });
  };

  return (
    <main className="editor shell">
      <header className="editor__header">
        <button
          type="button"
          className="icon-button"
          aria-label="ホームへ戻る"
          onClick={onBack}
        >
          ‹
        </button>
        <div className="file-identity">
          <strong title={project.fileName}>{project.fileName}</strong>
          <span>
            {formatFrames(project.durationFrames, project.sampleRate)}・
            {project.sampleRate / 1000}kHz・
            {project.channels === 1 ? "Mono" : `${project.channels}ch`}
          </span>
        </div>
        <div
          className="analysis-badge"
          title={
            waveformProgress < 1 &&
            (playbackState.playing ||
              playbackState.buffering ||
              precisionBoundary !== null)
              ? "音声再生を優先するため全体波形の解析を一時停止しています"
              : "全体波形の解析進捗"
          }
        >
          {waveformProgress < 1 &&
          (playbackState.playing ||
            playbackState.buffering ||
            precisionBoundary !== null)
            ? "再生優先"
            : waveformProgress < 1
              ? `${Math.round(waveformProgress * 100)}%`
              : "LOCAL"}
        </div>
      </header>

      <section className="waveform-section overview-section">
        <div className="section-label">
          <span>全体波形</span>
          <small>タップで移動</small>
        </div>
        <WaveformCanvas
          mode="overview"
          pyramid={overview}
          durationFrames={project.durationFrames}
          sampleRate={project.sampleRate}
          viewport={viewport}
          selection={project}
          playheadFrame={playbackState.currentFrame}
          onSeek={seek}
          label="録音全体の波形。タップするとその位置へ移動します"
        />
      </section>

      <section className="waveform-section detail-section">
        <div className="section-label">
          <span>詳細波形</span>
          <small>ドラッグで移動・ピンチで拡大</small>
        </div>
        <div className="detail-waveform-wrap">
          <WaveformCanvas
            mode="detail"
            pyramid={detailView.pyramid}
            dataStartFrame={detailView.dataStartFrame}
            waveformSource={detailView.source}
            loading={detailView.loading}
            durationFrames={project.durationFrames}
            sampleRate={project.sampleRate}
            viewport={viewport}
            selection={project}
            playheadFrame={playbackState.currentFrame}
            onSeek={seek}
            onViewportChange={persistViewport}
            label="詳細波形。波形を左右へ動かし、中央の赤線で位置を決めます"
          />
          <span className="cursor-label" aria-hidden="true">
            現在位置
          </span>
        </div>
        <button
          type="button"
          className="current-time"
          onClick={() => seek(viewport.centerFrame)}
        >
          {formatFrames(playbackState.currentFrame, project.sampleRate)}
        </button>
      </section>

      <section className="playback-controls" aria-label="再生操作">
        <button
          type="button"
          className="control-button"
          onClick={() => playback.skip(-5)}
          data-testid="editor.skipBack"
          aria-label="5秒戻る"
        >
          −5<span>秒</span>
        </button>
        <button
          type="button"
          className={`play-button ${playbackState.buffering ? "is-buffering" : ""}`}
          onClick={() => void playback.toggle()}
          data-testid="editor.play"
          aria-label={playbackState.playing ? "一時停止" : "再生"}
        >
          {playbackState.buffering ? "…" : playbackState.playing ? "Ⅱ" : "▶"}
        </button>
        <button
          type="button"
          className="control-button"
          onClick={() => playback.skip(5)}
          data-testid="editor.skipForward"
          aria-label="5秒進む"
        >
          ＋5<span>秒</span>
        </button>
        <label className="rate-picker">
          <span className="sr-only">再生速度</span>
          <select
            value={project.playbackRate}
            onChange={(event) =>
              setRate(
                Number(event.target.value) as EditingProject["playbackRate"],
              )
            }
          >
            {RATES.map((rate) => (
              <option key={rate} value={rate}>
                {rate}×
              </option>
            ))}
          </select>
        </label>
      </section>
      <p className="playback-status" role="status" aria-live="polite">
        {playbackState.buffering ? "再生位置を準備中…" : ""}
      </p>

      <section className="trim-section" aria-label="切り出す範囲">
        <BoundaryCard
          boundary="start"
          frame={project.startFrame}
          sampleRate={project.sampleRate}
          onSet={() => assignBoundary("start")}
          onPrecision={() => setPrecisionBoundary("start")}
        />
        <BoundaryCard
          boundary="end"
          frame={project.endFrame}
          sampleRate={project.sampleRate}
          onSet={() => assignBoundary("end")}
          onPrecision={() => setPrecisionBoundary("end")}
        />
        <div className="selection-duration">
          <span>選択時間</span>
          <strong>
            {formatFrames(
              project.endFrame - project.startFrame,
              project.sampleRate,
            )}
          </strong>
        </div>
      </section>

      <section className="preview-row" aria-label="選択範囲の確認">
        <button type="button" onClick={() => previewSelection("start")}>
          冒頭5秒
        </button>
        <button type="button" onClick={() => previewSelection("all")}>
          選択範囲
        </button>
        <button type="button" onClick={() => previewSelection("end")}>
          末尾5秒
        </button>
      </section>

      <footer className="editor__actions">
        <div className="history-controls">
          <button
            type="button"
            disabled={history.undo.length === 0}
            onClick={undo}
            data-testid="editor.undo"
            aria-label="元に戻す"
          >
            ↶<span>Undo</span>
          </button>
          <button
            type="button"
            disabled={history.redo.length === 0}
            onClick={redo}
            data-testid="editor.redo"
            aria-label="やり直す"
          >
            ↷<span>Redo</span>
          </button>
        </div>
        <button
          type="button"
          className="export-button"
          onClick={() => setShowExport(true)}
          data-testid="editor.export"
        >
          動画を作成 <span aria-hidden="true">›</span>
        </button>
      </footer>

      {precisionBoundary && (
        <PrecisionEditor
          boundary={precisionBoundary}
          project={project}
          file={file}
          waveformService={waveformService}
          overview={overview}
          playback={playback}
          onCancel={() => setPrecisionBoundary(null)}
          onConfirm={confirmPrecision}
          onDetailTiming={precisionDetailTiming}
          onError={onError}
        />
      )}

      {showExport && (
        <ExportSheet
          project={project}
          file={file}
          onProjectChange={onProjectChange}
          onClose={() => setShowExport(false)}
          onCompleted={exportCompleted}
          onError={onError}
        />
      )}
    </main>
  );
}

function BoundaryCard({
  boundary,
  frame,
  sampleRate,
  onSet,
  onPrecision,
}: {
  boundary: Boundary;
  frame: number;
  sampleRate: number;
  onSet: () => void;
  onPrecision: () => void;
}) {
  return (
    <article className={`boundary-card boundary-card--${boundary}`}>
      <button
        type="button"
        className="boundary-card__set"
        onClick={onSet}
        data-testid={`editor.${boundary}.set`}
      >
        <span className="boundary-card__label">{boundary.toUpperCase()}</span>
        <span>現在位置に設定</span>
      </button>
      <button
        type="button"
        className="boundary-card__precision"
        onClick={onPrecision}
        data-testid={`editor.${boundary}.value`}
      >
        <strong>{formatFrames(frame, sampleRate)}</strong>
        <span>
          微調整 <span aria-hidden="true">›</span>
        </span>
      </button>
    </article>
  );
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
